import styled from '@emotion/styled';
import { useCallback } from 'react';
import { Color } from '@diritto/common/style/color';
import { LocalStorageKey } from '@diritto/common/utils/localStorage';
import { ProfileImage } from 'app-components/accounts/profiles/ProfileImage';
import { useAuthState } from 'contexts/AuthProvider';
import { MessageTooltipIcon } from 'icons/MessageTooltipIcon';
import { UnknownProfileIcon } from 'icons/UnknownProfileIcon';
import { useGetUnreadChatMessageCount } from 'queries/chat/useGetUnreadChatMessageCount';
import { useShowIntroModal } from 'state-hooks/modal/useShowIntroModal';
import { IconButton } from 'ui-components/common/IconButton';
import { UserMenu } from 'ui-components/navigations/userMenu/UserMenu';
import { useClient } from 'ui-hooks/useClient';
import { useMenuRef } from 'ui-hooks/useMenuRef';

export const UserMenuToggleButton = () => {
  const { ref, isOpen, setOpen } = useMenuRef<HTMLDivElement>();
  const { isLoggedIn, profile, partner } = useAuthState();
  const { data: unloadCountData } = useGetUnreadChatMessageCount(isLoggedIn);
  const { isApp } = useClient();
  const newMessageBadge = unloadCountData && unloadCountData?.totalUnreadCount > 0;
  const { show, onHide } = useShowIntroModal(LocalStorageKey.MessageGuideTooltip);

  const handleClickItem = useCallback(() => {
    setOpen(prev => !prev);
    if (newMessageBadge && show) {
      onHide();
    }
  }, [newMessageBadge, onHide, setOpen, show]);

  return (
    <StyledContainer ref={ref}>
      <StyledImageWrapper onClick={handleClickItem}>
        {isLoggedIn ? (
          <>
            <ProfileImage file={profile?.profileImage} defaultImageKey={profile?.nickname} size={32} />
            {newMessageBadge ? (
              <>
                <StyledNewBadge />
                {show && <StyledMessageTooltipIcon />}
              </>
            ) : null}
          </>
        ) : (
          <UnknownProfileIcon />
        )}
      </StyledImageWrapper>
      {isOpen && (
        <StyledMenuWrapper>
          <UserMenu
            profileId={profile?.id}
            isLoggedIn={isLoggedIn}
            isApp={isApp}
            partner={partner}
            onBlur={() => setOpen(false)}
          />
        </StyledMenuWrapper>
      )}
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  position: relative;
  flex-direction: column;
`;

const StyledImageWrapper = styled(IconButton)`
  display: block;
  position: relative;
  width: 32px;
  height: 32px;

  & img {
    width: 100%;
    border-radius: 50%;
  }
`;

const StyledNewBadge = styled.div`
  width: 10px;
  height: 10px;
  position: absolute;
  right: 0;
  top: 0;
  border-radius: 50%;
  background-color: ${Color.Red500};
  border: 2px solid #fff;
  box-sizing: border-box;
  z-index: 1;
`;

const StyledMenuWrapper = styled.div`
  position: relative;
`;

const StyledMessageTooltipIcon = styled(MessageTooltipIcon)`
  position: absolute;
  top: 44px;
  right: 0;
  z-index: 1;
`;
